/**
 * Firestore CRUD for bidding take-off training examples (biddingTrainingExamples).
 *
 * Two producers write here:
 *  - the Mac-mini bidding agent (exportTakeoffTrainingExamples) → human take-off labels
 *  - CRM staff confirming/correcting the agent's timber extraction (this service)
 */

import { db } from './firebase';
import {
  collection,
  doc,
  setDoc,
  deleteDoc,
  onSnapshot,
  query,
  where,
  Timestamp,
} from 'firebase/firestore';
import type {
  BiddingTrainingExample,
  BiddingTrainingLabel,
  BiddingTimberItem,
} from '../types';
import { removeUndefinedDeep } from './removeUndefinedDeep';

const COLLECTION = 'biddingTrainingExamples';

function fromDoc(snap: { id: string; data: () => Record<string, unknown> }): BiddingTrainingExample {
  const d = snap.data();
  const created = d.createdAt as { toDate?: () => Date } | undefined;
  const updated = d.updatedAt as { toDate?: () => Date } | undefined;
  return {
    id: snap.id,
    leadId: d.leadId as string,
    dealId: (d.dealId as string) ?? null,
    documentId: (d.documentId as string) ?? null,
    documentUrl: (d.documentUrl as string) ?? null,
    pageNumber: typeof d.pageNumber === 'number' ? d.pageNumber : null,
    measurementId: (d.measurementId as string) ?? null,
    structureId: (d.structureId as string) ?? null,
    structureName: (d.structureName as string) ?? null,
    exampleType: (d.exampleType as BiddingTrainingExample['exampleType']) || 'staffConfirmation',
    label: (d.label as BiddingTrainingLabel) || {},
    agentItem: d.agentItem as BiddingTimberItem | undefined,
    analysisId: (d.analysisId as string) || undefined,
    source: (d.source as BiddingTrainingExample['source']) || 'crmStaff',
    status: d.status as BiddingTrainingExample['status'],
    userId: (d.userId as string) || undefined,
    userName: (d.userName as string) || undefined,
    createdAt: created?.toDate?.() ?? undefined,
    updatedAt: updated?.toDate?.() ?? undefined,
  };
}

/** Subscribe to every training example for a lead, newest first. */
export function subscribeLeadTrainingExamples(
  leadId: string,
  callback: (list: BiddingTrainingExample[]) => void,
): () => void {
  const q = query(collection(db, COLLECTION), where('leadId', '==', leadId));
  return onSnapshot(q, snap => {
    const list = snap.docs.map(d => fromDoc({ id: d.id, data: () => d.data() }));
    list.sort((a, b) => (b.createdAt?.getTime() ?? 0) - (a.createdAt?.getTime() ?? 0));
    callback(list);
  });
}

/** Build the canonical label from an agent timber item (the staff "confirm" path). */
export function labelFromTimberItem(item: BiddingTimberItem): BiddingTrainingLabel {
  const dims = item.dimensions
    || (item.width && item.height ? `${item.width}x${item.height}` : undefined);
  return removeUndefinedDeep({
    name: item.name,
    dimensions: dims,
    quantity: item.quantity,
    species: item.species || item.material,
    grade: item.grade,
    moistureContent: item.moistureContent,
  }) as BiddingTrainingLabel;
}

interface RecordTrainingExampleInput {
  leadId: string;
  analysisId?: string;
  agentItem: BiddingTimberItem;
  label: BiddingTrainingLabel;
  exampleType: 'staffConfirmation' | 'staffCorrection';
  userId: string;
  userName: string;
}

/**
 * Confirm or correct an agent-extracted timber item as a training example.
 * Deterministic id (analysisId + item id) keeps re-confirmations idempotent.
 */
export async function recordStaffTrainingExample(input: RecordTrainingExampleInput): Promise<string> {
  const key = `lead_${input.leadId}__item_${input.analysisId || 'na'}_${input.agentItem.id}`;
  const now = Timestamp.now();
  const payload = removeUndefinedDeep({
    leadId: input.leadId,
    analysisId: input.analysisId,
    documentId: input.agentItem.documentId ?? null,
    pageNumber: input.agentItem.pageNumber ?? null,
    exampleType: input.exampleType,
    status: input.exampleType === 'staffCorrection' ? 'corrected' : 'confirmed',
    label: input.label,
    agentItem: input.agentItem,
    source: 'crmStaff',
    userId: input.userId,
    userName: input.userName,
    updatedAt: now,
    createdAt: now,
  });
  await setDoc(doc(db, COLLECTION, key), payload, { merge: true });
  return key;
}

export async function deleteTrainingExample(id: string): Promise<void> {
  await deleteDoc(doc(db, COLLECTION, id));
}
